import axios from 'axios';

export const getToken = () => localStorage.getItem('toolboxToken') || '';
export const getNcToken = () => localStorage.getItem('amsToken') || '';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export interface CommercialFieldValues {
  name: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  employees: string;
  estimatedPayroll: string;
  revenue: string;
  areaSquareMeters: string;
  primaryNaicsCode: string;
  primarySICCode: string;
  yearEstablished: string;
  numberOfLocations: string;
}

export const EMPTY_FIELDS: CommercialFieldValues = {
  name: '', addressLine1: '', city: '', state: '', zipCode: '', phoneNumber: '',
  employees: '', estimatedPayroll: '', revenue: '', areaSquareMeters: '',
  primaryNaicsCode: '', primarySICCode: '', yearEstablished: '', numberOfLocations: '',
};

export interface FieldDef {
  key: keyof CommercialFieldValues;
  label: string;
  /** Fields NowCerts never returns via getexistingdetails, so AMS side is always blank. */
  alwaysOffDefault?: boolean;
}

export const FIELD_DEFS: FieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'addressLine1', label: 'Address Line 1' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
  { key: 'phoneNumber', label: 'Phone Number' },
  { key: 'employees', label: 'Estimated Employees' },
  { key: 'estimatedPayroll', label: 'Estimated Payroll' },
  { key: 'revenue', label: 'Estimated Revenue' },
  { key: 'areaSquareMeters', label: 'Area Square Meters', alwaysOffDefault: true },
  { key: 'primaryNaicsCode', label: 'Primary NAICS Code' },
  { key: 'primarySICCode', label: 'Primary SIC Code' },
  { key: 'yearEstablished', label: 'Year Established' },
  { key: 'numberOfLocations', label: 'Number of Locations', alwaysOffDefault: true },
];

/**
 * NowCerts and Fenris both sometimes return numeric fields (payroll, employee count, etc.)
 * as actual numbers rather than strings. CommercialFieldValues always stores strings, so
 * every value must be funneled through here before being assigned — otherwise a raw number
 * survives into state and later `.trim()` calls on it throw.
 */
export function toFieldString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function computeDefaultToggles(existing: CommercialFieldValues): Record<string, boolean> {
  const toggles: Record<string, boolean> = {};
  FIELD_DEFS.forEach((f) => {
    toggles[f.key] = f.alwaysOffDefault ? false : !toFieldString(existing[f.key]);
  });
  return toggles;
}

export function mapFenrisResponseToFields(fr: any): CommercialFieldValues {
  if (!fr) return { ...EMPTY_FIELDS };
  return {
    name: toFieldString(fr.name),
    addressLine1: toFieldString(fr.address?.addressLine1),
    city: toFieldString(fr.address?.city),
    state: toFieldString(fr.address?.state),
    zipCode: toFieldString(fr.address?.zipCode),
    phoneNumber: toFieldString(fr.phone),
    employees: fr.employees ? toFieldString(String(fr.employees).split('-')[1]) : '',
    estimatedPayroll: toFieldString(fr.estimatedPayroll),
    revenue: toFieldString(fr.estimatedRevenue),
    areaSquareMeters: toFieldString(fr.areaSquareMeters),
    primaryNaicsCode: toFieldString(fr.parent?.primaryNAICSCode),
    primarySICCode: fr.parent?.primarySICCode ? toFieldString(fr.parent.primarySICCode).substring(0, 4) : '',
    yearEstablished: toFieldString(fr.parent?.yearEstablished),
    numberOfLocations: toFieldString(fr.parent?.numberOfLocations),
  };
}

/* ── NowCerts client search / profile ── */

export const fetchClients = (query: string) =>
  axios.get(
    `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${getNcToken()}`,
    authHeaders(),
  );

export const fetchClientById = (insuredDatabaseId: string) =>
  axios.get(
    `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlistbyId?insuredDatabaseId=${insuredDatabaseId}&nowCertsToken=${getNcToken()}`,
    authHeaders(),
  );

export const fetchExistingClRatingDetails = (insuredId: string) =>
  axios.get(
    `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/getexistingdetails?insuredId=${insuredId}&ncToken=${getNcToken()}`,
    authHeaders(),
  );

/* ── Fenris enrichment ── */

export const fetchFenrisDetails = (
  name: string,
  addressLine1: string,
  state: string,
  city: string,
  zipCode: string,
  insuredId: string,
) =>
  axios.post(
    `${process.env.REACT_APP_DataFabric_Url}/dataenrich/details`,
    {
      Target: 'FENRIS',
      Data: {
        names: name,
        address: { addressLine1, state, city, zipCode },
        InsuredId: insuredId,
      },
    },
    authHeaders(),
  );

/* ── Update AMS + history ── */

export const updateInsuredDetails = (payload: Record<string, unknown>) =>
  axios.post(
    `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/updateinsureddetails?databaseId=${payload.DatabaseId}&ncToken=${getNcToken()}`,
    payload,
    authHeaders(),
  );

export const updateInsuredClRatingDetails = (payload: Record<string, unknown>) =>
  axios.post(
    `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/updateinsuredclratingdetails?ncToken=${getNcToken()}`,
    payload,
    authHeaders(),
  );

export const updateFenrisHistoryRecord = (payload: Record<string, unknown>) =>
  axios.post(`${process.env.REACT_APP_DataFabric_Url}/dataenrich/updatehistorybytransacionId`, payload, authHeaders());

export function buildUpdateInsuredPayload(
  databaseId: string,
  existing: CommercialFieldValues,
  fenris: CommercialFieldValues,
  toggles: Record<string, boolean>,
) {
  const pick = (key: keyof CommercialFieldValues) => (toggles[key] ? fenris[key] : existing[key]);
  return {
    DatabaseId: databaseId,
    insuredCommercialName: pick('name'),
    AddressLine1: pick('addressLine1'),
    City: pick('city'),
    State: pick('state'),
    ZipCode: pick('zipCode'),
    PhoneNumber: pick('phoneNumber'),
  };
}

export function buildUpdateClRatingPayload(
  databaseId: string,
  existing: CommercialFieldValues,
  fenris: CommercialFieldValues,
  toggles: Record<string, boolean>,
  description: string,
) {
  const pick = (key: keyof CommercialFieldValues) => (toggles[key] ? fenris[key] : existing[key]);
  return {
    DatabaseId: databaseId,
    sic: pick('primarySICCode'),
    yearBizStarted: String(pick('yearEstablished') || ''),
    numberOfFullTimeEmployees: pick('employees'),
    totalPayroll: parseInt(pick('estimatedPayroll') || '0', 10) || 0,
    naic: pick('primaryNaicsCode'),
    annualSales: parseFloat(pick('revenue') || '0') || 0,
    description: description || '',
    isActiveSicCode: !!toggles.primarySICCode,
  };
}

export function buildHistoryUpdatePayload(fenris: CommercialFieldValues, transactionId: string) {
  return {
    name: fenris.name,
    address: {
      addressLine1: fenris.addressLine1,
      city: fenris.city,
      state: fenris.state,
      zipCode: fenris.zipCode,
    },
    phone: fenris.phoneNumber,
    estimatedEmployees: parseInt(fenris.employees || '0', 10) || 0,
    estimatedpayroll: parseInt(fenris.estimatedPayroll || '0', 10) || 0,
    estimatedRevenue: parseInt(fenris.revenue || '0', 10) || 0,
    areaSquareMeters: parseInt(fenris.areaSquareMeters || '0', 10) || 0,
    parent: {
      primaryNAICSCode: fenris.primaryNaicsCode,
      primarySICCode: fenris.primarySICCode,
      yearEstablished: fenris.yearEstablished,
      numberOfLocations: parseInt(fenris.numberOfLocations || '0', 10) || 0,
    },
    TranscationId: transactionId,
  };
}

/* ── History list / feedback ── */

export const fetchFenrisHistoryList = (pageNumber: number, recordCount: number) =>
  axios.get(
    `${process.env.REACT_APP_DataFabric_Url}/dataenrich/getfenrishistory?pageNumber=${pageNumber}&recordCount=${recordCount}`,
    authHeaders(),
  );

export const fetchFenrisHistoryByTransactionId = (transactionId: string) =>
  axios.get(`${process.env.REACT_APP_DataFabric_Url}/dataenrich/getfenrishistorybytransacionId?transactionId=${transactionId}`, authHeaders());

export const submitLikeDislike = (transactionId: string, like: boolean, disLike: boolean, feedbackMessage = '') =>
  axios.post(
    `${process.env.REACT_APP_DataFabric_Url}/dataenrich/likedislike?transactionId=${transactionId}`,
    { like, disLike, feedbackMessage },
    authHeaders(),
  );

export const submitFenrisFeedback = (transactionId: string, feedbackMessage: string) =>
  axios.post(
    `${process.env.REACT_APP_DataFabric_Url}/dataenrich/fenrisfeedback?transactionId=${transactionId}`,
    { feedbackMessage },
    authHeaders(),
  );
